import { useRef, useState } from 'react';

/**
 * Reordenar linhas de tabela arrastando pela alça (a "mãozinha").
 *
 * Usa Pointer Events em vez do drag and drop nativo do HTML: o nativo não dispara
 * em tela de toque, e boa parte das bancas configura o painel pelo celular. O
 * ponteiro fica preso na alça (setPointerCapture) e a linha de destino é achada
 * pelo que está embaixo do dedo/cursor — por isso cada <tr> precisa das props de
 * `linha(indice)`.
 *
 * O hook não mexe na lista: ao soltar ele só chama `aoSoltar(origem, destino)`,
 * com os índices das linhas na ordem em que aparecem na tela.
 */
const ATRIBUTO = 'data-linha-arrastavel';
// distância da borda (da caixa com rolagem ou da tela) em que ela rola sozinha no arraste
const MARGEM_ROLAGEM = 60;

export default function useArrastarLinhas(aoSoltar) {
	const [arrastando, setArrastando] = useState(null);
	const [alvo, setAlvo] = useState(null);
	// o alvo também em ref: o pointerup lê o valor na hora, sem esperar render
	const alvoRef = useRef(null);
	// caixa com rolagem própria em volta da tabela (null = a página)
	const rolagemRef = useRef(null);

	const encerrar = () => {
		alvoRef.current = null;
		setArrastando(null);
		setAlvo(null);
	};

	const caixaRolavel = (elemento) => {
		for (let atual = elemento.parentElement; atual && atual !== document.body; atual = atual.parentElement) {
			const { overflowY } = window.getComputedStyle(atual);

			if ((overflowY === 'auto' || overflowY === 'scroll') && atual.scrollHeight > atual.clientHeight) {
				return atual;
			}
		}

		return null;
	};

	const rolarPerto = (y) => {
		const caixa = rolagemRef.current;
		const topo = caixa ? caixa.getBoundingClientRect().top : 0;
		const base = caixa ? caixa.getBoundingClientRect().bottom : window.innerHeight;
		const passo = y < topo + MARGEM_ROLAGEM ? -10 : (y > base - MARGEM_ROLAGEM ? 10 : 0);

		if (passo !== 0) {
			(caixa || window).scrollBy(0, passo);
		}
	};

	const linhaSob = (x, y) => {
		const elemento = document.elementFromPoint(x, y);
		const linha = elemento && elemento.closest(`[${ATRIBUTO}]`);

		return linha ? Number(linha.getAttribute(ATRIBUTO)) : null;
	};

	const alca = (indice, desabilitado = false) => ({
		style: { touchAction: 'none', cursor: desabilitado ? 'default' : (arrastando === null ? 'grab' : 'grabbing') },
		onPointerDown: (e) => {
			if (desabilitado || (e.pointerType === 'mouse' && e.button !== 0)) {
				return;
			}

			e.preventDefault();
			e.currentTarget.setPointerCapture(e.pointerId);
			rolagemRef.current = caixaRolavel(e.currentTarget);
			alvoRef.current = indice;
			setArrastando(indice);
			setAlvo(indice);
		},
		onPointerMove: (e) => {
			if (arrastando === null) {
				return;
			}

			rolarPerto(e.clientY);

			const destino = linhaSob(e.clientX, e.clientY);

			if (destino !== null && destino !== alvoRef.current) {
				alvoRef.current = destino;
				setAlvo(destino);
			}
		},
		onPointerUp: () => {
			if (arrastando === null) {
				return;
			}

			const destino = alvoRef.current;

			if (destino !== null && destino !== arrastando) {
				aoSoltar(arrastando, destino);
			}

			encerrar();
		},
		onPointerCancel: encerrar,
	});

	const linha = (indice) => ({ [ATRIBUTO]: indice });

	return { arrastando, alvo, alca, linha };
}
